import i18next from "i18next";
import { useContext } from "react";
import { useMutation, useQueryClient } from "react-query";

import { useClient } from "utils/client";
import { ControlContext } from "context/Controls";
import { TrackingContext } from "context/Tracking";

import { getAIRiskApi, isTimeoutError } from "../api";
import { AIGenerateMode, AIHazardShell } from "../types";
import { mergeHazardIntoSessionCache } from "./sessionCache";

interface GenerateAIHazardDetailVariables {
  hazardId: string;
  mode: AIGenerateMode;
  // Freshest shell values — sent so unsaved edits are honored; the server
  // persists them onto the shell (contract endpoint 4).
  name: string;
  description: string;
  user_context: string;
  regeneration_context?: string;
}

interface GenerateAIHazardDetailContext {
  startedAt: number;
}

// Stage 2: synchronous POST /hazards/:hazardId/generate (client timeout
// 240 s, see api.ts). mode "initial" persists the draft (status →
// generated); mode "regenerate" persists a pending_proposal — both are
// persisted server-side BEFORE the response, so merging the returned shell
// into the cache mirrors the durable state.
export const useGenerateAIHazardDetail = (projectId: string) => {
  const client = useClient();
  const queryClient = useQueryClient();
  const { track } = useContext(TrackingContext);
  const { displayNotification } = useContext(ControlContext);

  return useMutation<
    AIHazardShell,
    Error,
    GenerateAIHazardDetailVariables,
    GenerateAIHazardDetailContext
  >(
    ({ hazardId, mode, name, description, user_context, regeneration_context }) =>
      getAIRiskApi(client).generateHazardDetail(projectId, hazardId, {
        language: i18next.language,
        mode,
        name,
        description,
        user_context,
        regeneration_context,
      }),
    {
      onMutate: () => ({ startedAt: Date.now() }),
      onSuccess: (hazard, { mode }, context) => {
        mergeHazardIntoSessionCache(queryClient, projectId, hazard);

        track("ai risk: generate detail", {
          mode,
          duration_ms: Date.now() - (context?.startedAt ?? Date.now()),
        });
      },
      onError: (error) => {
        track("ai risk: generate detail failed", { message: error.message });

        displayNotification!(
          i18next.t(
            isTimeoutError(error)
              ? "risks.ai.errors.timeout"
              : "risks.ai.errors.generationFailed"
          ),
          error.message,
          "error"
        );
      },
    }
  );
};
